import { GenericLazyString } from '@eventbrite/i18n';
import React, { FunctionComponent } from 'react';
import { TicketsFormat } from '../../../constants/statsig';
import {
    getTicketForImage,
    getTicketImageFullSizeUrl,
    getTicketImageThumbnailUrl,
} from '../../../models/tickets';
import { UrgencySignals } from '../../../models/urgencySignals';
import { isDonationTicket } from '../../../utils/tickets';
import TicketDisplayCardContent, {
    TicketDisplayCardContentProps,
} from '../TicketDisplayCardContent';
import TicketDisplayCardContentCompactSize, {
    TicketDisplayCardContentCompactSizeProps,
} from '../TicketDisplayCardContentCompactSize';
import TicketDisplayCardContentFullSize, {
    TicketDisplayCardContentFullSizeProps,
} from '../TicketDisplayCardContentFullSize';

export interface TicketDisplayCardContentSwitcherProps
    extends Pick<
            TicketDisplayCardContentProps,
            | 'category'
            | 'currencyFormat'
            | 'currencySymbol'
            | 'donationAmount'
            | 'hideSelect'
            | 'image'
            | 'includeFee'
            | 'includeTax'
            | 'isCurrencySuffix'
            | 'isFormSubmitting'
            | 'isTicketTiered'
            | 'onDonationChange'
            | 'onJoinWaitlist'
            | 'onQuantityChange'
            | 'paymentConstraints'
            | 'primaryPrice'
            | 'shouldHideStatus'
            | 'showJoinWaitlist'
            | 'ticketReseller'
            | 'variantId'
            | 'variants'
        >,
        Pick<
            TicketDisplayCardContentCompactSizeProps,
            | 'description'
            | 'displayPrice'
            | 'feeAndTax'
            | 'hideSaleDates'
            | 'id'
            | 'isDisabled'
            | 'name'
            | 'quantityRemaining'
            | 'selectedQuantity'
            | 'ticketOptionsRange'
        > {
    statusKey: TicketDisplayCardContentCompactSizeProps['ticketStatus'];
    isTicketSelectionCompactSizeFlow: boolean;
    salesEnd: TicketDisplayCardContentCompactSizeProps['formattedSalesEndDate'];
    salesStart: string | GenericLazyString;
    isDisabled: boolean;
    ticketsFormat: TicketsFormat;
    urgencySignals?: UrgencySignals;
}

const TicketDisplayCardContentSwitcher: FunctionComponent<
    TicketDisplayCardContentSwitcherProps
> = (props) => {
    const ticketForImage = getTicketForImage(props) as unknown as
        | typeof props
        | undefined;
    const image: TicketDisplayCardContentFullSizeProps['image'] =
        ticketForImage && {
            ticketName: ticketForImage.name.toString(),
            fullSizeUrl: getTicketImageFullSizeUrl(ticketForImage),
            thumbnailUrl: getTicketImageThumbnailUrl(ticketForImage),
        };
    const isDonation = isDonationTicket({
        category: props.category,
    });

    const onQuantityChange = (quantity: number) => {
        props.onQuantityChange({ id: props.id, quantity });
    };

    if (props.isTicketSelectionCompactSizeFlow) {
        return (
            <TicketDisplayCardContentCompactSize
                description={props.description}
                displayPrice={props.displayPrice ?? ''}
                feeAndTax={props.feeAndTax ?? []}
                isDisabled={props.isDisabled}
                id={props.id}
                name={props.name.toString()}
                onQuantityChange={onQuantityChange}
                quantityRemaining={props.quantityRemaining}
                selectedQuantity={props.selectedQuantity}
                ticketStatus={props.statusKey}
                formattedSalesEndDate={props.salesEnd}
                ticketOptionsRange={props.ticketOptionsRange}
                hideSaleDates={props.hideSaleDates}
            />
        );
    }

    return props.ticketsFormat === TicketsFormat.card ? (
        <TicketDisplayCardContentFullSize
            category={props.category}
            currencyFormat={props.currencyFormat}
            currencySymbol={props.currencySymbol}
            description={props.description}
            donationAmount={props.donationAmount}
            feesAndTaxes={props.feeAndTax ?? []}
            formattedSalesEndDate={props.salesEnd}
            formattedSalesStartDate={props.salesStart}
            hideSaleDates={props.hideSaleDates}
            hideSelect={props.hideSelect}
            image={image}
            includeFee={props.includeFee}
            includeTax={props.includeTax}
            isCurrencySuffix={props.isCurrencySuffix}
            isDonation={isDonation}
            name={props.name}
            onDonationChange={props.onDonationChange}
            onQuantityChange={onQuantityChange}
            price={props.displayPrice}
            priceWithoutDiscounts={props.primaryPrice}
            remainingQuantity={props.quantityRemaining}
            selectableQuantities={props.ticketOptionsRange}
            selectedQuantity={props.selectedQuantity}
            ticketId={props.id}
            ticketStatus={props.statusKey}
            urgencySignals={props.urgencySignals}
        />
    ) : (
        <TicketDisplayCardContent
            category={props.category}
            currencyFormat={props.currencyFormat}
            currencySymbol={props.currencySymbol}
            description={props.description}
            displayPrice={props.displayPrice}
            donationAmount={props.donationAmount}
            feeAndTax={props.feeAndTax}
            hideSaleDates={props.hideSaleDates}
            hideSelect={props.hideSelect}
            id={props.id}
            image={props.image}
            includeFee={props.includeFee}
            includeTax={props.includeTax}
            isCurrencySuffix={props.isCurrencySuffix}
            isFormSubmitting={props.isFormSubmitting}
            isTicketTiered={props.isTicketTiered}
            name={props.name}
            onDonationChange={props.onDonationChange}
            onJoinWaitlist={props.onJoinWaitlist}
            onQuantityChange={props.onQuantityChange}
            paymentConstraints={props.paymentConstraints}
            primaryPrice={props.primaryPrice}
            quantityRemaining={props.quantityRemaining}
            salesEnd={props.salesEnd}
            salesStart={props.salesStart}
            selectedQuantity={props.selectedQuantity}
            shouldHideStatus={props.shouldHideStatus}
            showJoinWaitlist={props.showJoinWaitlist}
            statusKey={props.statusKey}
            ticketOptionsRange={props.ticketOptionsRange}
            ticketReseller={props.ticketReseller}
            variantId={props.variantId}
            variants={props.variants}
        />
    );
};

export default TicketDisplayCardContentSwitcher;
